import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/ApiUrl';
import { Search, Package, MapPin, Star, ChevronRight, Loader, X } from 'lucide-react';

export const UnifiedSearchDropdown = ({ onDispensarySelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({ products: [], dispensaries: [] });
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [userLocation, setUserLocation] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Import API_URL - add this at the top of your ac

  // Request user location only when user searches
  const requestUserLocation = () => {
    if (locationRequested || userLocation) return; // Don't request again if already done
    
    setLocationRequested(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied or unavailable');
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  };

  // API call to search both products and dispensaries
  const searchItems = async (query) => {
    if (!query.trim()) {
      setResults({ products: [], dispensaries: [] });
      return;
    }

    // Request location when user actually searches
    requestUserLocation();

    setIsLoading(true);
    
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        limit: '500'
      });
      
      // Add user location if available for dispensary distance calculation
      if (userLocation) {
        params.append('lat', userLocation.lat.toString());
        params.append('lng', userLocation.lng.toString());
      }
      
      const response = await fetch(`${API_URL}/api/search?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      if (data.success) {
        setResults({
          products: data.products || [],
          dispensaries: data.dispensaries || []
        });
      } else {
        throw new Error(data.message || 'Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults({ products: [], dispensaries: [] });
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchItems(searchTerm);
      } else {
        setResults({ products: [], dispensaries: [] });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, userLocation]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      const totalItems = results.products.length + results.dispensaries.length;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => prev < totalItems - 1 ? prev + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : totalItems - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            handleItemSelect(selectedIndex);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, results]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemSelect = (index) => {
    const totalProducts = results.products.length;
    
    if (index < totalProducts) {
      // Product selected
      const product = results.products[index];
      navigate(`/products/${product.id}`);
    } else {
      // Dispensary selected
      const dispensary = results.dispensaries[index - totalProducts];
      if (onDispensarySelect) {
        onDispensarySelect(dispensary);
      }
    }
    
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (searchTerm) {
      setIsOpen(true);
    }
  };

  const handleInputClick = () => {
    // Open dropdown on click
    if (searchTerm) {
      setIsOpen(true);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults({ products: [], dispensaries: [] });
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const totalResults = results.products.length + results.dispensaries.length;
  const hasResults = totalResults > 0;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      {/* Search Input */}
      <div style={{
        backgroundColor: '#1a1a1a',
        border: `1px solid ${isOpen ? '#00d4aa' : '#333'}`,
        borderRadius: '0.75rem',
        padding: '0.625rem 1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        minWidth: 'clamp(10rem, 20vw, 15rem)',
        maxWidth: '16.5rem',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxShadow: isOpen ? '0 0 0 4px rgba(0, 212, 170, 0.1)' : 'none'
      }}>
        <Search size={16} style={{ 
          color: isOpen ? '#00d4aa' : '#666',
          flexShrink: 0
        }} />
        
        <input
          ref={searchRef}
          type="text"
          placeholder="Products & dispensaries..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'white',
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            width: '100%',
            fontFamily: "'Inter', sans-serif"
          }}
        />
        
        {searchTerm && (
          <button
            onClick={clearSearch}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#00d4aa'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && (searchTerm || isLoading) && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {isLoading ? (
            <div style={{
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#888'
            }}>
              <Loader size={16} style={{ 
                animation: 'spin 1s linear infinite',
                marginRight: '8px',
                color: '#00d4aa'
              }} />
              Searching...
            </div>
          ) : hasResults ? (
            <div>
              {/* Products Section */}
              {results.products.length > 0 && (
                <div>
                  <div style={{
                    padding: '12px 16px 8px',
                    borderBottom: '1px solid #333',
                    color: '#888',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Products ({results.products.length})
                  </div>
                  
                  {results.products.map((product, index) => (
                    <div
                      key={`product-${product.id}`}
                      onClick={() => handleItemSelect(index)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: index === results.products.length - 1 && results.dispensaries.length > 0 ? '1px solid #333' : 'none',
                        backgroundColor: selectedIndex === index ? 'rgba(0, 212, 170, 0.1)' : 'transparent',
                        transition: 'background-color 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        backgroundColor: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Package size={16} style={{ color: '#666' }} />
                        )}
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: '600',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          marginBottom: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {product.name}
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px',
                          color: '#888'
                        }}>
                          <span>{product.brand}</span>
                          <span>•</span>
                          <span>{product.type}</span>
                          {product.rating > 0 && (
                            <>
                              <span>•</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <Star size={10} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                                <span>{product.rating}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div style={{
                        fontWeight: '600',
                        color: '#00d4aa',
                        fontSize: '14px'
                      }}>
                        {product.price}
                      </div>
                      
                      <ChevronRight size={14} style={{ color: '#666', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Dispensaries Section */}
              {results.dispensaries.length > 0 && (
                <div>
                  <div style={{
                    padding: '12px 16px 8px',
                    borderBottom: results.dispensaries.length > 0 ? '1px solid #333' : 'none',
                    color: '#888',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Dispensaries ({results.dispensaries.length})
                  </div>
                  
                  {results.dispensaries.map((dispensary, index) => {
                    const globalIndex = results.products.length + index;
                    return (
                      <div
                        key={`dispensary-${dispensary.id}`}
                        onClick={() => handleItemSelect(globalIndex)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          backgroundColor: selectedIndex === globalIndex ? 'rgba(0, 212, 170, 0.1)' : 'transparent',
                          transition: 'background-color 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          backgroundColor: '#333',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <MapPin size={16} style={{ color: '#00d4aa' }} />
                        </div>
                        
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: '600',
                            color: '#e5e5e5',
                            fontSize: '14px',
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {dispensary.name}
                          </div>
                          
                          <div style={{
                            fontSize: '12px',
                            color: '#888',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {dispensary.city}, {dispensary.state}
                            {dispensary.distance && (
                              <span style={{ color: '#00d4aa', marginLeft: '8px' }}>
                                {dispensary.distance} mi
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <ChevronRight size={14} style={{ color: '#666', flexShrink: 0 }} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : searchTerm && !isLoading ? (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: '#888',
              fontSize: '14px'
            }}>
              No results found for "{searchTerm}"
            </div>
          ) : null}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};